import Table, { TableBody, TableHeader, TableHeaderColumn } from '@rio-cloud/rio-uikit/Table';
import type { TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import classNames from 'classnames';
import { type ReactNode, type Ref, useMemo } from 'react';
import type { IntlShape } from 'react-intl';

import type { Tour } from '#domain/Tour.ts';
import type { Vehicle } from '#domain/Vehicle.ts';
import type { TourWithSortableKey } from '#utils/tours.util.ts';

import { NoDataView } from '../shared/NoDataView.tsx';
import { TourEmptyState } from './TourEmptyState.tsx';
import { TourTableRow } from './TourTableRow.tsx';

type TourTableProps = {
    activeTourId?: string;
    hasNoTours: boolean;
    columns: TourTableColumnConfig[];
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnResize: (columnKey: string, width?: number | string) => void;
    onRowClick: (tourId: string) => void;
    onSortChange: (columnKey: keyof TourWithSortableKey, direction: 'asc' | 'desc') => void;
    onViewTypeChange: (viewType: TableViewTogglesViewType) => void;
    sortDirection: 'asc' | 'desc';
    sortKey: keyof TourWithSortableKey;
    tableRef: Ref<HTMLDivElement>;
    tours: Tour[];
    vehicles: Vehicle[];
    viewType: TableViewTogglesViewType;
};

export type TourTableColumnKey =
    | 'searchName'
    | 'fleetGroupName'
    | 'licensePlate'
    | 'firstStopEndTarget'
    | 'lastStopEndTarget'
    | 'progressPercentage'
    | 'statusWeight';

export type TourTableColumnConfig = {
    key: TourTableColumnKey;
    label: ReactNode;
    disabled?: boolean;
    draggable?: boolean;
    visible: boolean;
    resizeable?: boolean;
    sortable?: boolean;
    width?: number | string;
};

export const getTourTableColumns = (intl: IntlShape): TourTableColumnConfig[] => [
    {
        key: 'searchName',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.table.header.tour',
            defaultMessage: 'Tour',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 200,
    },
    {
        key: 'firstStopEndTarget',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.table.header.tourStart',
            defaultMessage: 'Tour start',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 300,
    },
    {
        key: 'lastStopEndTarget',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.table.header.tourEnd',
            defaultMessage: 'Tour end',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 300,
    },
    {
        key: 'licensePlate',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.table.header.vehicle',
            defaultMessage: 'Vehicle',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 150,
    },
    {
        key: 'fleetGroupName',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.table.header.group',
            defaultMessage: 'Group',
        }),
        draggable: true,
        visible: false,
        resizeable: true,
        sortable: true,
        width: 150,
    },
    {
        key: 'progressPercentage',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.table.header.progress',
            defaultMessage: 'Progress percentage',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 200,
    },
    {
        key: 'statusWeight',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.table.header.status',
            defaultMessage: 'Status',
        }),
        draggable: true,
        visible: true,
    },
];

export const TourTable = ({
    activeTourId,
    hasNoTours,
    columns,
    onColumnOrderChange,
    onColumnResize,
    onRowClick,
    onSortChange,
    onViewTypeChange,
    sortDirection,
    sortKey,
    tableRef,
    tours,
    vehicles,
    viewType,
}: TourTableProps) => {
    const isTableEmpty = tours.length <= 0;
    const visibleColumns = columns.filter(column => column.visible !== false);
    const visibleColumnKeys = useMemo(() => visibleColumns.map(column => column.key), [visibleColumns]);

    return (
        <Table
            ref={tableRef}
            rowKey='id'
            className={classNames('table-head-filled table-column-overflow-hidden')}
            hover={!isTableEmpty}
            activeRowId={activeTourId}
            viewType={viewType}
            sortBy={String(sortKey)}
            sortDirection={sortDirection}
            onSortChange={(columnKey, direction) => onSortChange(columnKey as keyof TourWithSortableKey, direction)}
            columnOrder={columns.map(column => column.key)}
            onColumnOrderChange={onColumnOrderChange}
            onColumnResize={onColumnResize}
            onViewTypeChange={onViewTypeChange}
            onRowClick={row => onRowClick(String(row.id))}
            noRowsState={hasNoTours ? <TourEmptyState /> : <NoDataView />}
            responsiveBreakpoint={{ list: '800px' }}
        >
            <TableHeader sticky>
                {visibleColumns.map(column => (
                    <TableHeaderColumn
                        key={column.key}
                        columnKey={column.key}
                        width={column.width}
                        sortable={column.sortable}
                        resizeable={column.resizeable}
                        draggable={column.draggable}
                    >
                        {column.label}
                    </TableHeaderColumn>
                ))}
            </TableHeader>
            <TableBody>
                {tours.map(tour => (
                    <TourTableRow
                        key={tour.id}
                        tour={tour}
                        vehicle={vehicles.find(vehicle => vehicle.id === tour.truck.id)}
                        visibleColumnKeys={visibleColumnKeys}
                    />
                ))}
            </TableBody>
        </Table>
    );
};
